import { createContext, useEffect, useState, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import type { AuthTokens, LoginInput, RegisterInput, User } from '@fantasy/types';
import { api, attemptRefresh, clearSession, getRefreshToken, setAuthExpiredListener, setSession } from '../api.ts';
import { identifyUser, resetIdentity } from '../lib/analytics.ts';

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: User | null;
}

export interface AuthContextValue extends AuthState {
  login: (input: LoginInput) => Promise<void>;
  register: (input: RegisterInput) => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/** On mount, trades a persisted refresh token for a fresh session rather
 * than requiring login on every reload. Returns null if there is no stored
 * refresh token, or if it no longer works.
 *
 * Goes through `attemptRefresh`'s single-flight guard rather than calling
 * `api.auth.refresh` directly — this effect runs twice under React
 * StrictMode's dev-mode double-invoke, and the refresh token is single-use
 * server-side, so two independent calls with the same stored token would
 * have one rotate it successfully and the other get rejected as already-used
 * — clearing the session the first call had just restored. */
const GOOGLE_CALLBACK_PATH = '/auth/google/callback';

/** Google's sign-in redirect is a full-page browser navigation the API
 * sends here with `accessToken`/`refreshToken` in the URL fragment — never
 * the query string, so they never land in a server access log. Reads them
 * once and immediately replaces the URL, so a later reload of this page
 * can't try to reuse a fragment that's already been consumed. */
function consumeGoogleCallbackTokens(): AuthTokens | null {
  if (window.location.pathname !== GOOGLE_CALLBACK_PATH) return null;

  const params = new URLSearchParams(window.location.hash.slice(1));
  const accessToken = params.get('accessToken');
  const refreshToken = params.get('refreshToken');
  window.history.replaceState(null, '', '/');

  if (!accessToken || !refreshToken) return null;
  return { accessToken, refreshToken };
}

async function restoreSession(): Promise<User | null> {
  if (!getRefreshToken()) return null;

  const refreshed = await attemptRefresh();
  if (!refreshed) {
    clearSession();
    return null;
  }

  try {
    const { user } = await api.auth.me();
    return user;
  } catch {
    clearSession();
    return null;
  }
}

/** Finishes a Google sign-in redirect if one just landed, otherwise falls
 * back to restoring a session from a previously stored refresh token. */
async function bootstrapSession(): Promise<User | null> {
  const googleTokens = consumeGoogleCallbackTokens();
  if (!googleTokens) return restoreSession();

  setSession(googleTokens);
  try {
    const { user } = await api.auth.me();
    return user;
  } catch {
    clearSession();
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading', user: null });

  useEffect(() => {
    let cancelled = false;
    bootstrapSession().then((user) => {
      if (cancelled) return;
      setState(user ? { status: 'authenticated', user } : { status: 'unauthenticated', user: null });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setAuthExpiredListener(() => setState({ status: 'unauthenticated', user: null }));
    return () => setAuthExpiredListener(null);
  }, []);

  // One place reacting to the auth state, rather than repeating this at
  // every call site that can produce it (mount-restore, login, register,
  // logout, session-expiry) — ties PostHog's autocaptured events and
  // Sentry's crash reports to the same person id regardless of which path
  // got them there. A bare id only, matching both files' own "no PII"
  // choice — never email or name.
  useEffect(() => {
    if (state.status === 'authenticated' && state.user) {
      identifyUser(state.user.id, state.user.email);
      Sentry.setUser({ id: state.user.id });
    } else if (state.status === 'unauthenticated') {
      resetIdentity();
      Sentry.setUser(null);
    }
  }, [state.status, state.user]);

  async function login(input: LoginInput) {
    const { user, tokens } = await api.auth.login(input);
    setSession(tokens);
    setState({ status: 'authenticated', user });
  }

  async function register(input: RegisterInput) {
    const { user, tokens } = await api.auth.register(input);
    setSession(tokens);
    setState({ status: 'authenticated', user });
  }

  async function logout() {
    const token = getRefreshToken();
    clearSession();
    setState({ status: 'unauthenticated', user: null });
    // Best-effort: revoke server-side, but the client is already signed out
    // regardless of whether this succeeds.
    if (token) await api.auth.logout(token).catch(() => {});
  }

  const value: AuthContextValue = { ...state, login, register, logout };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
